import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { add, fromMinor, parse, zero, type Money as MoneyValue } from '@fint/money';
import { BarChart3, PieChart, TrendingUp, Wallet } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { Period } from '@/lib/dates';
import {
  Card, CardContent, CardHeader, CardTitle, ColourDot, Donut, ErrorState, HBars, LineChart, Money, StackedBars, changeText, moneyText, signedText,
} from '@/features/common/ui';
import type { Basis } from '@/features/dashboard/shared';
import { monthName } from './overview-months';
import {
  anyMovement,
  betweenBooksMinor,
  booksMoving,
  cashRows,
  combinedBars,
  isChartsResponse,
  missingBooks,
  monthlyNet,
  owedRows,
  perBookBars,
  ringChoices,
  ringSlices,
  runningLines,
  summarise,
  type ChartsResponse,
} from './overview-charts';

/**
 * The Overview's charts: the same books, drawn.
 *
 * ── WHY A SECOND QUERY ──
 * The table above comes from `/practice/overview`, which is also the MCP
 * tool's response and is budgeted by the byte. The charts want a monthly
 * series per book and a category split per book, which would triple it for
 * a caller that wanted one row per book - so they have their own route, the
 * same query string, and the same period picker, and this component loads
 * and fails on its own. A chart that cannot load leaves the table standing.
 *
 * ── THE ORDER ──
 * Time first, then shares, then position: how the months went (in and out,
 * then the running profit), where the money went and came from, and what
 * each book holds and is owed today. Each answers the next question a person
 * has after the last.
 *
 * ── WHAT IT WILL NOT DO WITH MONEY ──
 * Amounts reach the kit as integers of minor units through `parse`; the
 * few sums this file makes for a summary sentence go through `add`. Nothing
 * here passes an amount through `Number()`.
 */
export function OverviewCharts({ period, basis, organisationId }: { period: Period; basis: Basis; organisationId?: string }) {
  const query = useQuery({
    // No org id in the key: the charts, like the table, are keyed to the
    // person rather than the tenant - and to the organisation being drawn.
    queryKey: ['practice-overview-charts', organisationId ?? null, period.from, period.to, basis],
    queryFn: () => api.get<unknown>(`/practice/overview/charts${qs({ from: period.from, to: period.to, basis, organisationId })}`),
  });
  const [mode, setMode] = useState<'books' | 'combined'>('books');
  const [eachBook, setEachBook] = useState(true);
  const [ring, setRing] = useState<'all' | string>('all');

  if (query.isError) {
    return (
      <Card>
        <ErrorState error={query.error} onRetry={() => { void query.refetch(); }} />
      </Card>
    );
  }
  if (query.isPending) {
    return (
      <div className="space-y-4" aria-busy="true" data-testid="overview-charts-loading">
        <div className="skeleton h-72 rounded-lg" />
        <div className="skeleton h-64 rounded-lg" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="skeleton h-56 rounded-lg" />
          <div className="skeleton h-56 rounded-lg" />
        </div>
      </div>
    );
  }
  const data = query.data;
  if (!isChartsResponse(data)) {
    // A body of another shape - an older server, a mocked route - is a
    // stated failure, not a blank chart or a thrown render.
    return (
      <Card>
        <ErrorState error={new Error('The charts came back in a shape this page does not understand. Reload to try again.')} />
      </Card>
    );
  }

  const currency = data.currency;
  const minor = (amount: string) => parse(amount, currency).minor;
  const sum = (pick: (row: ChartsResponse['totals']['months'][number]) => string): MoneyValue =>
    data.totals.months.reduce((total, row) => add(total, parse(pick(row), currency)), zero(currency));
  const totalIn = sum((row) => row.income);
  const totalOut = sum((row) => row.expense);
  const between = betweenBooksMinor(data, minor);
  const missing = missingBooks(data);
  const moved = anyMovement(data, minor);
  const empty = {
    title: `Nothing moved in ${period.label}`,
    description: 'An invoice, an expense or a deposit dated in this period will show here.',
  };

  const bars = mode === 'books' ? perBookBars(data, minor) : combinedBars(data, minor);
  const lines = runningLines(data, minor, eachBook);
  const runningEnd = data.totals.months[data.totals.months.length - 1]?.cumulativeProfit ?? '0';
  const choices = ringChoices(data);
  const ringId = choices.some((choice) => choice.id === ring) ? ring : 'all';
  const ringName = choices.find((choice) => choice.id === ringId)?.name ?? 'All books';
  const spent = ringSlices(data, 'expense', ringId, minor);
  const earned = ringSlices(data, 'income', ringId, minor);
  const cash = cashRows(data, minor);
  const owed = owedRows(data, minor);

  /**
   * What the two month charts say after their own series.
   *
   * ── WHY ──
   * The per-book charts answer, on every bar, how this window compared with
   * the last one and how much is behind it; the Overview's said the figure
   * alone, which left the reader of a good month with the two questions the
   * page exists to answer - against what, and which book (reporting-audit
   * P5). The response already carries both; nothing new is fetched.
   *
   * ── WHY NO "CLICK OPENS" ROW ──
   * The per-book contract ends with where a click goes. A month across
   * several books has no one list behind it - it is two or three books'
   * months at once - so there is no click, and a row promising one would be
   * worse than the row being absent.
   */
  const net = monthlyNet(data, minor);
  const monthRows = (i: number) => {
    const rows = [];
    if (i > 0) {
      rows.push({
        key: 'prev',
        label: `vs ${monthName(data.months[i - 1] ?? '')}`,
        value: changeText(net[i] ?? 0, net[i - 1] ?? 0, currency),
      });
    }
    const { moved: active, of } = booksMoving(data, data.months[i] ?? '', minor);
    rows.push({ key: 'books', label: 'Books moving', value: `${active} of ${of}` });
    return rows;
  };

  return (
    <div className="space-y-4" data-testid="overview-charts">
      {/* (a) */}
      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>
            <BarChart3 className="size-4 text-muted-foreground" aria-hidden />
            Money in and out, month by month
          </CardTitle>
          <Segmented
            label="Show the bars"
            value={mode}
            onChange={setMode}
            options={[
              ['books', 'Per book'],
              ['combined', 'Combined'],
            ]}
          />
        </CardHeader>
        <CardContent>
          <StackedBars
            months={bars.months}
            groups={bars.groups}
            line={bars.line}
            currency={currency}
            title="Money in and out, month by month"
            summary={summarise(
              data,
              mode === 'books' ? 'Money in and out by month, per book' : 'Money in and out by month, combined',
              `${moneyText(totalIn.minor, currency)} in and ${moneyText(totalOut.minor, currency)} out, net of money between books`,
            )}
            empty={empty}
            extraRows={monthRows}
          />
          {moved && between > 0 ? (
            <Caption>
              The net profit line and the combined bars are net of <Money money={fromMinor(between, currency)} currency={currency} showSymbol={false} />{' '}
              {currency} that moved between your books; each book&rsquo;s own bars still include it.
            </Caption>
          ) : null}
          <Missing missing={missing} />
        </CardContent>
      </Card>

      {/* (b) */}
      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>
            <TrendingUp className="size-4 text-muted-foreground" aria-hidden />
            Profit, running
          </CardTitle>
          <button
            type="button"
            aria-pressed={eachBook}
            onClick={() => setEachBook((value) => !value)}
            className={chip(eachBook)}
          >
            Each book
          </button>
        </CardHeader>
        <CardContent>
          <LineChart
            months={data.months}
            lines={lines}
            currency={currency}
            title="Profit, running"
            summary={summarise(
              data,
              'Running net profit',
              `ending at ${moneyText(minor(runningEnd), currency)}${eachBook ? ', with each book as its own line' : ''}`,
            )}
            empty={empty}
            /* A running total's next question is how much of it was this month:
               the step the line took, then what that was against the step before. */
            extraRows={(i) => [
              { key: 'step', label: 'This month', value: signedText(net[i] ?? 0, currency) },
              ...monthRows(i),
            ]}
          />
          {moved && data.months.length === 1 ? (
            // The page opens on this month, so this is the first thing most
            // people see here: one month is one point, and the run is a
            // period away.
            <Caption>One month is one point. Pick This FY, or a quarter, to see how the profit built up.</Caption>
          ) : null}
          {moved && between > 0 ? (
            <Caption>The running total across books is net of money that moved between them.</Caption>
          ) : null}
          <Missing missing={missing} />
        </CardContent>
      </Card>

      {/* (c) */}
      <Card>
        <CardHeader className="gap-3">
          <CardTitle>
            <PieChart className="size-4 text-muted-foreground" aria-hidden />
            Where the money went, and where it came from
          </CardTitle>
          <div role="group" aria-label="Which books the rings show" className="flex flex-wrap gap-1.5" data-testid="ring-books">
            {choices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                aria-pressed={ringId === choice.id}
                onClick={() => setRing(choice.id)}
                className={cn(chip(ringId === choice.id), 'gap-1.5')}
              >
                {choice.colour ? <ColourDot colour={choice.colour} className="h-2 w-2" /> : null}
                {choice.name}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2" data-testid="rings">
          <section aria-labelledby="ring-spent" className="min-w-0 space-y-3">
            <h4 id="ring-spent" className="text-sm font-medium">
              Where the money went <span className="font-normal text-muted-foreground">· {ringName}</span>
            </h4>
            <Donut
              slices={spent}
              currency={currency}
              title={`Where the money went, ${ringName}`}
              summary={summarise(data, `Money out by category for ${ringName}`, `${spent.length} ${spent.length === 1 ? 'slice' : 'slices'}, the largest ${spent[0]?.name ?? 'none'}`)}
              centreLabel="out"
              empty={{ title: 'Nothing spent', description: `No expense dated in ${period.label} to split by category.` }}
            />
          </section>
          <section aria-labelledby="ring-earned" className="min-w-0 space-y-3">
            <h4 id="ring-earned" className="text-sm font-medium">
              Where it came from <span className="font-normal text-muted-foreground">· {ringName}</span>
            </h4>
            <Donut
              slices={earned}
              currency={currency}
              title={`Where it came from, ${ringName}`}
              summary={summarise(data, `Money in by category for ${ringName}`, `${earned.length} ${earned.length === 1 ? 'slice' : 'slices'}, the largest ${earned[0]?.name ?? 'none'}`)}
              centreLabel="in"
              empty={{ title: 'Nothing earned', description: `No invoice or categorised deposit dated in ${period.label} to split by category.` }}
            />
          </section>
        </CardContent>
        {ringId === 'all' && between > 0 ? (
          <CardContent className="pt-0">
            <Caption>Across all books, categories that are money between your books are left out, so the rings add up to the totals above.</Caption>
          </CardContent>
        ) : null}
      </Card>

      {/* (d) */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>
              <Wallet className="size-4 text-muted-foreground" aria-hidden />
              Cash on hand by book
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HBars
              rows={cash}
              currency={currency}
              title="Cash on hand by book"
              valueLabel="Cash"
              summary={summarise(data, 'Cash on hand today', `${moneyText(minor(data.totals.cash.total), currency)} across ${data.totals.cash.accounts} ${data.totals.cash.accounts === 1 ? 'account' : 'accounts'}`)}
              empty={{ title: 'No cash to show', description: 'Bank accounts with a balance appear here, as of today.' }}
            />
            <Missing missing={missing} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              <Wallet className="size-4 text-muted-foreground" aria-hidden />
              Owed to you by book
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HBars
              rows={owed}
              currency={currency}
              title="Owed to you by book"
              valueLabel="Receivable"
              partLabel="Overdue"
              summary={summarise(data, 'Owed to you today', `${moneyText(minor(data.totals.receivables), currency)} receivable, of which ${moneyText(minor(data.totals.overdue), currency)} overdue`)}
              empty={{ title: 'Nothing owed to you', description: 'Sent invoices with a balance due appear here, as of today.' }}
            />
            <Missing missing={missing} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** A line of small print under a chart. */
function Caption({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-xs text-muted-foreground">{children}</p>;
}

/**
 * The books a chart is missing, named. A withheld book is not drawn as
 * zeros - "you may not see this" and "nothing happened" are different facts
 * - and a book in another currency is not drawn on an axis in this one.
 */
function Missing({ missing }: { missing: ReturnType<typeof missingBooks> }) {
  if (missing.length === 0) return null;
  return (
    <p className="mt-3 text-xs text-muted-foreground" data-testid="chart-missing">
      {missing.map((book, i) => (
        <span key={book.name}>
          {i > 0 ? ' ' : ''}
          {book.name} is not in this chart
          {book.reason === 'withheld' ? ' — see the notes under the table.' : ' — it keeps its books in another currency.'}
        </span>
      ))}
    </p>
  );
}

/**
 * The chip style the period picker's quick chips use, so the page's
 * controls read as one family. An inactive chip keeps a hairline border:
 * the book chips over the rings wrap onto a second row on a phone, and a
 * borderless "Acme Consulting LLP" alone on a line read as a label rather
 * than as something to tap. Tall enough for a thumb.
 */
function chip(active: boolean): string {
  return cn(
    'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors touch:min-h-11 touch:min-w-11 touch:px-4',
    active ? 'bg-primary/10 text-primary' : 'border border-input text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent',
  );
}

function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: Array<[T, string]>;
}) {
  return (
    <div role="group" aria-label={label} className="flex gap-1.5">
      {options.map(([key, text]) => (
        <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)} className={chip(value === key)}>
          {text}
        </button>
      ))}
    </div>
  );
}
